/**
 * Virgin EOD scheduling conflict — the boundary between a SCHEDULING problem
 * and a CONTINUATION of started work.
 *
 * ============================================================================
 * THE PRODUCT DECISION THIS ENCODES
 * ============================================================================
 * A virgin / never-started ticket that can no longer finish inside today's
 * allowed technician EOD window is NOT a rollover ticket.
 *
 * W4 (field test 2026-10-16, DECISION_LEDGER §1) refines this: it is not
 * AUTOMATICALLY a rollover. It becomes one only by a recorded, human-approved
 * conversion (dynamic continuation) — the server raises the end-of-day decision
 * (rollover_conversions, RolloverDecisionPanel) and only `decide_rollover_conversion`
 * with an authorized person's JWT converts it, never create-eod-rollover and never
 * this event. Until then it is a scheduling conflict, flagged as NOT an authorized
 * schedule (tickets.schedule_state = 'conflict').
 *
 * Rollover / EOD continuation exists for work that has ACTUALLY STARTED and
 * has remaining production work. Bucket 3 §3.12.1 says so in its own
 * eligibility list — an EOD continuation may be valid when "Ticket has been
 * started · Ticket has active remaining work" — and excludes "Tickets with no
 * active production commitment".
 *
 * So a virgin ticket in this condition stays exactly what it is:
 *
 *   - virgin (`actually_started` stays false)
 *   - never-started (`actual_start_time` stays null)
 *   - in its existing non-terminal lifecycle
 *   - dynamically scheduled, with Start Time still obeying the no-past-time
 *     invariant (Bucket 1 §1.1, Bucket 2 §2.8.1, both Final Non-Negotiables)
 *
 * Its OptiFinish may legitimately project past the technician's shift end —
 * flagged as an unapproved overrun with a decision raised (W4). That is honest planning truth, and the schedule saying so out loud is the
 * point. What must NOT happen is the product freezing Start Time in the past
 * to avoid admitting it.
 *
 * ============================================================================
 * WHY THE EVENT NAME IS DIFFERENT, NOT A FLAG
 * ============================================================================
 * The started-work path dispatches `eod-exceeded`, which reaches a modal whose
 * primary action is "Convert to Rollover" — it creates a Part B, writes
 * rollover lineage, and calls the create-eod-rollover edge. Every one of those
 * is wrong for a ticket that has never been touched.
 *
 * A boolean on the shared event would keep both consequences reachable from
 * one payload, and "prove the virgin case can never expose Convert to
 * Rollover" would then rest on a conditional somebody can invert. A separate
 * event name routes the virgin case to a component that does not CONTAIN the
 * rollover action, so the guarantee is structural rather than conditional.
 *
 * ============================================================================
 * VIRGINITY IS READ CANONICALLY
 * ============================================================================
 * `classifyEodConflict` delegates to `ticketVirginityHelpers`, the single
 * canonical source (`actually_started !== true`). It does not read status
 * strings, timer_status, or position in a control-flow branch. A caller that
 * happens to sit inside an already-virgin branch still asks, so a future edit
 * to that branch's gate cannot silently hand a started ticket the virgin
 * semantics.
 */
import { isTicketVirgin, type VirginityCheckFields } from '@/utils/ticketVirginityHelpers';

/**
 * A virgin ticket cannot finish inside today's allowed EOD window.
 * Consequence: a SCHEDULING decision (reschedule / reassign). Never a rollover.
 */
export const VIRGIN_EOD_EXCEEDED_EVENT = 'virgin-eod-exceeded';

/**
 * Started work will not finish inside today's allowed EOD window.
 * Consequence: the existing rollover / EOD continuation workflow. UNCHANGED.
 */
export const STARTED_WORK_EOD_EXCEEDED_EVENT = 'eod-exceeded';

/**
 * Detail carried by `eod-exceeded` — ONE typed contract for every opener of the carry modal (timer controls, the
 * EOD monitor, OptiFinish pushback, the EOD checker, AutoBump, and the end-of-day decision panel).
 *
 * FLOYD 2026-10-26 (DEFECT 2): the decision panel used to put `String(detection.boundary_at ?? '')` — an ISO
 * instant, or '' — into `techEOD`, which the modal parsed as "HH:mm"; the parse failed and every approval of
 * started work was refused for `missing_shift_window`. Time now has two typed fields with one meaning each, and
 * the carry is planned from neither: the modal reads the work's own window from the shared carry planner.
 */
export interface StartedWorkEodExceededDetail {
  ticketId: string;
  customerName: string;
  techName: string;
  /** DISPLAY ONLY — the opener's wording of the end of day ("HH:mm" or ''). Never parsed into a plan. */
  techEOD: string;
  /**
   * The server's authorized end-of-day INSTANT for the work's day (ISO-8601; authorized overtime included), when
   * the opener has one — the decision panel's `detection.boundary_at`. Null/absent: the dated shift end applies.
   */
  authorizedBoundaryAt?: string | null;
  /** How far past the end of day the work runs, as the opener measured it (0 when not measured). Display only. */
  rolloverMinutes: number;
  ticketType?: string | null;
  /** Which surface asked (diagnostics). */
  trigger?: string;
  onResumeAnyway?: () => Promise<void>;
}

export type EodConflictKind =
  /** Virgin ticket, no work performed. A scheduling problem. */
  | 'virgin_scheduling_conflict'
  /** Work has actually started. A continuation problem. */
  | 'started_work_rollover';

/**
 * Detail carried by `virgin-eod-exceeded`.
 *
 * Deliberately does NOT carry `rolloverMinutes` or any remaining-work figure:
 * there is no work to remain. `minutesPastShiftEnd` describes the SCHEDULE
 * overshoot, which is the only quantity that is true here.
 */
export interface VirginEodSchedulingConflictDetail {
  ticketId: string;
  customerName: string;
  techName: string;
  /** The technician's shift end, as configured. Display only. */
  techEOD: string;
  /** How far the projected finish lands past that shift end. */
  minutesPastShiftEnd: number;
  ticketType?: string | null;
}

/**
 * Which EOD condition is this? Canonical virginity decides — nothing else.
 */
export function classifyEodConflict(ticket: VirginityCheckFields): EodConflictKind {
  return isTicketVirgin(ticket)
    ? 'virgin_scheduling_conflict'
    : 'started_work_rollover';
}

/** The event name a given conflict kind must be published under. */
export function eodConflictEventName(kind: EodConflictKind): string {
  return kind === 'virgin_scheduling_conflict'
    ? VIRGIN_EOD_EXCEEDED_EVENT
    : STARTED_WORK_EOD_EXCEEDED_EVENT;
}

/**
 * Does this conflict permit the started-work rollover consequence?
 *
 * Exists so the answer is one function rather than a repeated inline
 * comparison, and so a test can assert the virgin case answers `false`
 * without rendering anything.
 */
export function allowsRolloverConversion(kind: EodConflictKind): boolean {
  return kind === 'started_work_rollover';
}

/**
 * What the card's EOD gate should SAY, given who is reading it.
 *
 * The gate names an act, and not every viewer can perform that act. Rendering
 * "Needs rollover decision" to an assigned technician — which is what shipped —
 * asks them to resolve something they have no authority over and, on a virgin
 * ticket, no rollover to resolve.
 *
 * Bucket 5 §5.11 lists what technician-facing UI should make clear, and it is
 * risk: "EOD risk · Due Out risk · Waiter risk · Warnings/escalations". §5.6.8
 * then bounds what a warning may do — it "must not directly modify Start Time,
 * Tech OptiTime, OptiFinish, Cascade behavior, Bay occupancy, Timer history".
 * A technician gets the fact. The decision belongs to placement authority
 * (Bucket 3 §3.13.3's "Require user decision", §3.9.3's placement
 * accountability).
 *
 * Pure and total, so the boundary can be proven without mounting TicketCard —
 * which needs a shop context, a live Supabase client, a timezone provider and
 * a dozen children, and is the reason the surrounding card surface is pinned
 * structurally rather than by rendering.
 */
export type EodGatePresentation =
  /** The gate is not raised. */
  | 'none'
  /** No placement authority: a neutral timing fact, no decision implied. */
  | 'extends_past_shift'
  /** Placement authority + virgin: a SCHEDULING decision. */
  | 'scheduling_decision'
  /** Placement authority + started work: the existing rollover decision. */
  | 'rollover_decision';

export function resolveEodGatePresentation(input: {
  /** `derivedTicketTime`'s gate — finish crosses shift end + willingness. */
  requiresRolloverGate: boolean;
  /** The placement-authority boundary (`usePermissions().canAssignTickets`). */
  canAssignTickets: boolean;
  ticket: VirginityCheckFields;
}): EodGatePresentation {
  if (!input.requiresRolloverGate) return 'none';
  // Authority is checked BEFORE virginity: a technician gets the same neutral
  // wording either way, because neither decision is theirs to make.
  if (!input.canAssignTickets) return 'extends_past_shift';
  return classifyEodConflict(input.ticket) === 'virgin_scheduling_conflict'
    ? 'scheduling_decision'
    : 'rollover_decision';
}

/**
 * Detecting the conflict must never suppress the canonical bump.
 *
 * Bucket 3 §3.13.3 requires Core to "Warn the user… Require user decision".
 * Bucket 1 §1.1 and Bucket 2 §2.8.1 require the virgin anchor never to remain
 * scheduled in the past. Both are obligations; the repaired branch satisfies
 * both. This constant exists so the intent is greppable from the call site.
 */
export const VIRGIN_EOD_CONFLICT_DOES_NOT_BLOCK_BUMP = true;
